import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Feature, FeatureCollection, MultiPolygon, Point, Polygon } from "geojson";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import type { Job } from "../../../models";
import {
	JobMapProps,
	JobMapViewModel,
	PopupData,
	PolygonMetadata,
	PolygonRenderData,
	PolylineRenderData,
	MarkerRenderData,
	DEFAULT_CENTER,
	DEFAULT_ZOOM,
	LAYER_COLORS,
} from "./JobMap.types";
import {
	getJobColor,
	getShapeFeatureProperty,
	parseChangeInfo,
	isMarkerVisible,
	getJobBoundaryGeometry,
	extractPathsFromGeometry,
	getAllCoordinatesFromGeometry,
	generateHatchLines,
} from "./JobMap.helpers";
import {
	BOUNDARY_COLORS,
	NEARBY_JOB_COLOR,
	HATCH_ANGLES,
	HATCH_LINE_SPACING_METERS,
	UNMATCHED_POINT_COLOR,
	UNMATCHED_ZONE_COLOR,
	SELECTED_COLOR,
	SELECTED_OPACITY,
} from "../../SubmitJobs/Methods/Bulk/wizard/shared/constants";

const INTERNAL_SELECTED_COLOR = "#ff0000";
const INTERNAL_SELECTED_OPACITY = 0.6;

export function useJobMapViewModel(props: JobMapProps): JobMapViewModel {
	const [mapPoints, setMapPoints] = useState<MarkerRenderData[]>([]);
	const [mapPolygons, setMapPolygons] = useState<PolygonRenderData[]>([]);
	const [mapPolylines, setMapPolylines] = useState<PolylineRenderData[]>([]);
	const [visibleMapMarkers, setVisibleMapMarkers] = useState<MarkerRenderData[]>([]);
	const [visibleMapPolygons, setVisibleMapPolygons] = useState<PolygonRenderData[]>([]);
	const [center] = useState<google.maps.LatLngLiteral>(DEFAULT_CENTER);
	const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
	const [bounds, setBounds] = useState<google.maps.LatLngBoundsLiteral | undefined>(undefined);
	const [activePopup, setActivePopup] = useState<PopupData | null>(null);
	const [polygonMetadata, setPolygonMetadata] = useState<PolygonMetadata[]>([]);

	// Toolbar state (internal lasso + selection when showToolbar is active)
	const [internalLassoMode, setInternalLassoMode] = useState(false);
	const [internalSelectedFeatureIds, setInternalSelectedFeatureIds] = useState<Set<string>>(new Set());

	// Refs for lasso drawing state (avoids React re-renders during drawing)
	const lassoPathRef = useRef<google.maps.LatLngLiteral[]>([]);
	const completeLassoRef = useRef<() => void>(() => {});

	const mapsLibrary = useMapsLibrary("maps");
	const geometryLibrary = useMapsLibrary("geometry");
	const map = useMap();

	// Resolve props with defaults
	const effectiveJobs = useMemo(() => {
		if (props.jobs) return props.jobs;
		if (props.job) return [props.job];
		return [];
	}, [props.jobs, props.job]);

	const colorMode = props.colorMode ?? "per-job";
	const disableDefaultUI = props.disableDefaultUI ?? false;
	const showEmptyState = props.showEmptyState ?? false;
	const zoomControl = props.zoomControl ?? true;
	const boundsPadding = props.boundsPadding;
	const gestureHandling = props.gestureHandling;

	// Toolbar: resolve effective lasso/selection state
	const showToolbar = props.showToolbar ?? false;

	const effectiveLassoMode = showToolbar ? internalLassoMode : (props.lassoMode ?? false);

	const effectiveSelectedFeatureIds =
		props.selectedFeatureIds ?? (showToolbar ? internalSelectedFeatureIds : new Set<string>());

	const setEffectiveSelectedFeatureIds = props.onSelectedFeaturesChange ?? setInternalSelectedFeatureIds;

	const isBoundaryMode = colorMode === "boundary-hatch" || colorMode === "boundary-outline";

	const resolvedHeight = useMemo(() => {
		if (props.height === undefined) return undefined;
		if (typeof props.height === "number") return `${props.height}px`;
		return props.height;
	}, [props.height]);

	const hasGeodata = useMemo(() => {
		if (isBoundaryMode) {
			// For boundary modes, check boundary-specific locations
			return effectiveJobs.some((job) => getJobBoundaryGeometry(job) !== null);
		}
		return effectiveJobs.some(
			(job) =>
				(job.files?.boundaryGeoJSON?.features?.length ?? 0) > 0 ||
				(job.files?.pointsGeoJSON?.features?.length ?? 0) > 0 ||
				(job.files?.zonesGeoJSON?.features?.length ?? 0) > 0,
		);
	}, [effectiveJobs, isBoundaryMode]);

	const selectedFeatureIdsKey = useMemo(() => {
		if (!effectiveSelectedFeatureIds || effectiveSelectedFeatureIds.size === 0) return "";
		return Array.from(effectiveSelectedFeatureIds).sort().join(",");
	}, [effectiveSelectedFeatureIds]);

	// ── Boundary mode processing (boundary-hatch / boundary-outline) ─────────
	useEffect(() => {
		if (!map || !mapsLibrary || !isBoundaryMode) return;

		let cancelled = false;

		async function processBoundaryMode() {
			const mapBounds = new google.maps.LatLngBounds();
			const allPolygons: PolygonRenderData[] = [];
			const allPolylines: PolylineRenderData[] = [];
			const allPoints: MarkerRenderData[] = [];
			const allMetadata: PolygonMetadata[] = [];

			const isDuplicateFeaturesMode = colorMode === "boundary-outline";

			// ── 1. Render main job boundaries ──
			const hatchPromises: Promise<void>[] = [];

			effectiveJobs.forEach((job, index) => {
				const isHidden = props.hiddenJobIds?.includes(job.id);
				const boundaryGeom = getJobBoundaryGeometry(job);

				if (boundaryGeom) {
					const paths = extractPathsFromGeometry(boundaryGeom);
					const color = BOUNDARY_COLORS[index % BOUNDARY_COLORS.length];

					// Always collect coordinates for bounds
					const geomCoords = getAllCoordinatesFromGeometry(boundaryGeom);
					geomCoords.forEach((c) => mapBounds.extend(new google.maps.LatLng(c.lat, c.lng)));

					if (!isHidden) {
						if (isDuplicateFeaturesMode) {
							// Outline only (Polyline, not filled Polygon)
							paths.forEach((path, pathIndex) => {
								const closedPath = [...path, path[0]];
								allPolylines.push({
									id: `outline-${job.id}-${pathIndex}`,
									path: closedPath,
									strokeColor: color,
									strokeWeight: 3,
									strokeOpacity: 1,
								});
							});
						} else {
							// Filled polygon
							paths.forEach((path, pathIndex) => {
								allPolygons.push({
									id: `job-${job.id}-${pathIndex}`,
									paths: path,
									fillColor: color,
									fillOpacity: 0.4,
									strokeColor: color,
									strokeWeight: 3,
									strokeOpacity: 1,
								});
							});
						}

						// Generate hatch lines (async)
						const angle = HATCH_ANGLES[index % HATCH_ANGLES.length];
						hatchPromises.push(
							generateHatchLines(boundaryGeom, angle, HATCH_LINE_SPACING_METERS).then((hatchLines) => {
								hatchLines.forEach((linePath, lineIndex) => {
									allPolylines.push({
										id: `hatch-${job.id}-${lineIndex}`,
										path: linePath,
										strokeColor: color,
										strokeWeight: 1.5,
										strokeOpacity: 0.8,
									});
								});
							}),
						);
					}
				}
			});

			// ── 2. Render nearby job boundaries (grayed out) ──
			props.nearbyJobs?.forEach((nearby) => {
				const isHidden = props.hiddenNearbyJobIds?.includes(nearby.job.id);
				const boundaryGeom = getJobBoundaryGeometry(nearby.job);

				if (boundaryGeom) {
					const paths = extractPathsFromGeometry(boundaryGeom);
					const geomCoords = getAllCoordinatesFromGeometry(boundaryGeom);
					geomCoords.forEach((c) => mapBounds.extend(new google.maps.LatLng(c.lat, c.lng)));

					if (!isHidden) {
						paths.forEach((path, pathIndex) => {
							allPolygons.push({
								id: `nearby-${nearby.job.id}-${pathIndex}`,
								paths: path,
								fillColor: NEARBY_JOB_COLOR,
								fillOpacity: 0.2,
								strokeColor: NEARBY_JOB_COLOR,
								strokeWeight: 2,
								strokeOpacity: 0.5,
							});
						});
					}
				}
			});

			// ── 3. Render unmatched features ──
			if (props.unmatchedFeatures?.features) {
				props.unmatchedFeatures.features.forEach((feature, index) => {
					const geom = feature.geometry;
					if (!geom) return;

					if (geom.type === "Point") {
						const point = geom as Point;
						const position = { lat: point.coordinates[1], lng: point.coordinates[0] };
						const featureId = `unmatched-point-${index}`;
						const isSelected = effectiveSelectedFeatureIds?.has(featureId);

						mapBounds.extend(new google.maps.LatLng(position.lat, position.lng));
						allMetadata.push({ id: featureId, paths: [position] });

						if (!props.hideUnmatchedFeatures) {
							allPoints.push({
								id: featureId,
								position,
								iconColor: undefined,
								sampleId: "",
								barcode: "",
								hasChange: false,
								changeType: "",
								changeReason: "",
								dotStyle: {
									size: 14,
									color: isSelected ? SELECTED_COLOR : UNMATCHED_POINT_COLOR,
									border: isSelected ? "3px solid white" : "2px solid white",
									shadow: isSelected ? "0 2px 8px rgba(255,0,0,0.5)" : "0 2px 4px rgba(0,0,0,0.3)",
								},
							});
						}
					} else if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
						const paths = extractPathsFromGeometry(geom as Polygon | MultiPolygon);
						const geomCoords = getAllCoordinatesFromGeometry(geom as Polygon | MultiPolygon);
						geomCoords.forEach((c) => mapBounds.extend(new google.maps.LatLng(c.lat, c.lng)));

						if (!props.hideUnmatchedFeatures) {
							paths.forEach((path, pathIndex) => {
								const featureId = `unmatched-zone-${index}-${pathIndex}`;
								const isSelected = effectiveSelectedFeatureIds?.has(featureId);

								allMetadata.push({ id: featureId, paths: path });

								allPolygons.push({
									id: featureId,
									paths: path,
									fillColor: isSelected ? SELECTED_COLOR : UNMATCHED_ZONE_COLOR,
									fillOpacity: isSelected ? SELECTED_OPACITY : 0.4,
									strokeColor: isSelected ? SELECTED_COLOR : UNMATCHED_ZONE_COLOR,
									strokeWeight: isSelected ? 4 : 3,
									strokeOpacity: 1,
									clickable: !effectiveLassoMode,
								});
							});
						}
					}
				});
			}

			// ── 4. Render duplicate features ──
			if (props.duplicateFeatures) {
				const sortedFeatures = [...props.duplicateFeatures].sort((a, b) => (b.size ?? 14) - (a.size ?? 14));

				sortedFeatures.forEach((featureData) => {
					const { feature, color, size, groupId, featureIndex } = featureData;
					const featureId = `${groupId}-${featureIndex}`;
					const isHidden = props.hiddenDuplicateFeatureIds?.has(featureId);

					if (isHidden) return;

					const geom = feature.geometry;
					if (!geom) return;

					if (geom.type === "Point") {
						const point = geom as Point;
						const position = { lat: point.coordinates[1], lng: point.coordinates[0] };
						mapBounds.extend(new google.maps.LatLng(position.lat, position.lng));

						const markerSize = size || 14;
						const zIndex = 100 - markerSize;

						allPoints.push({
							id: `duplicate-${featureId}`,
							position,
							iconColor: undefined,
							sampleId: "",
							barcode: "",
							hasChange: false,
							changeType: "",
							changeReason: "",
							dotStyle: {
								size: markerSize,
								color,
								border: "2px solid white",
								shadow: "0 2px 4px rgba(0,0,0,0.3)",
								zIndex,
							},
						});
					} else if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
						const paths = extractPathsFromGeometry(geom as Polygon | MultiPolygon);
						const geomCoords = getAllCoordinatesFromGeometry(geom as Polygon | MultiPolygon);
						geomCoords.forEach((c) => mapBounds.extend(new google.maps.LatLng(c.lat, c.lng)));

						paths.forEach((path, pathIndex) => {
							allPolygons.push({
								id: `duplicate-${featureId}-${pathIndex}`,
								paths: path,
								fillColor: color,
								fillOpacity: 0.4,
								strokeColor: color,
								strokeWeight: 3,
								strokeOpacity: 1,
							});
						});
					}
				});
			}

			// ── 5. Highlight geometry (bounds only) ──
			if (props.highlightGeometry?.geometry) {
				const geom = props.highlightGeometry.geometry;
				if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
					const geomCoords = getAllCoordinatesFromGeometry(geom as Polygon | MultiPolygon);
					geomCoords.forEach((c) => mapBounds.extend(new google.maps.LatLng(c.lat, c.lng)));
				}
			}

			// Wait for all hatch line generation to complete
			await Promise.all(hatchPromises);

			if (cancelled) return;

			setMapPoints(allPoints);
			setMapPolygons(allPolygons);
			setMapPolylines(allPolylines);
			setPolygonMetadata(allMetadata);

			// Fit bounds
			const hasData = allPolygons.length > 0 || allPolylines.length > 0 || allPoints.length > 0;
			if (map && hasData) {
				const padding = boundsPadding ?? { top: 50, right: 50, bottom: 50, left: 50 };
				google.maps.event.addListenerOnce(map, "idle", () => {
					map.fitBounds(mapBounds, padding);
				});
				map.fitBounds(mapBounds, padding);
			}
		}

		processBoundaryMode();

		return () => {
			cancelled = true;
		};
	}, [
		effectiveJobs,
		selectedFeatureIdsKey,
		effectiveLassoMode,
		map,
		mapsLibrary,
		colorMode,
		boundsPadding,
		isBoundaryMode,
		props.hiddenJobIds,
		props.nearbyJobs,
		props.hiddenNearbyJobIds,
		props.unmatchedFeatures,
		props.hideUnmatchedFeatures,
		props.duplicateFeatures,
		props.hiddenDuplicateFeatureIds,
		props.highlightGeometry,
	]);

	// ── Standard mode processing (layer-type / per-job) ──────────────────────
	useEffect(() => {
		if (!map || !mapsLibrary || isBoundaryMode) return;

		const mapBounds = new google.maps.LatLngBounds();
		const allPolygons: PolygonRenderData[] = [];
		const allPoints: MarkerRenderData[] = [];
		const allMetadata: PolygonMetadata[] = [];

		// Helper function to process a polygon/multipolygon feature collection
		function processPolygonFeatures(
			featureCollection: FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>,
			job: Job,
			jobId: string,
			typePrefix: string,
		) {
			featureCollection.features?.forEach((feature: Feature<Polygon | MultiPolygon | Point>, featureIndex) => {
				if (!feature) return;

				if (feature.geometry.type !== "Polygon" && feature.geometry.type !== "MultiPolygon") {
					return;
				}

				const coordinates =
					feature.geometry.type === "Polygon" ? [feature.geometry.coordinates] : feature.geometry.coordinates;
				const paths = coordinates.map((coordinate) => {
					return coordinate
						.map((c) => {
							return c
								.map((cc) => {
									return { lat: cc[1], lng: cc[0] };
								})
								.flat();
						})
						.flat();
				});

				// Extend bounds from actual coordinates (bbox is optional per GeoJSON spec)
				paths.forEach((path) => {
					path.forEach((point) => {
						mapBounds.extend(new google.maps.LatLng(point.lat, point.lng));
					});
				});

				paths.forEach((path, pathIndex) => {
					const polygonId = `${typePrefix}-${jobId}-${featureIndex}-${pathIndex}`;
					const isSelected = effectiveSelectedFeatureIds?.has(polygonId);

					// Color based on mode
					const layerColor =
						typePrefix === "boundary"
							? LAYER_COLORS.boundary
							: typePrefix === "zones"
								? LAYER_COLORS.zones
								: LAYER_COLORS.points;
					const baseColor = colorMode === "layer-type" ? layerColor : getJobColor(jobId);

					allMetadata.push({ id: polygonId, paths: path });

					allPolygons.push({
						id: polygonId,
						paths: path,
						fillOpacity: isSelected ? INTERNAL_SELECTED_OPACITY : 0.4,
						fillColor: isSelected ? INTERNAL_SELECTED_COLOR : baseColor,
						strokeColor: isSelected ? INTERNAL_SELECTED_COLOR : colorMode === "layer-type" ? baseColor : undefined,
						job,
					});
				});
			});
		}

		// Helper function to process point features
		function processPointFeatures(featureCollection: FeatureCollection<Point>, jobId: string) {
			featureCollection.features?.forEach((feature: Feature<Point>, featureIndex) => {
				if (feature.geometry.type !== "Point") {
					return;
				}

				const pointPosition = { lat: feature.geometry.coordinates[1], lng: feature.geometry.coordinates[0] };
				mapBounds.extend(new google.maps.LatLng(pointPosition.lat, pointPosition.lng));

				const pointId = `points-${jobId}-${featureIndex}`;
				const isSelected = effectiveSelectedFeatureIds?.has(pointId);

				// Add point to metadata for lasso selection (single point as path)
				allMetadata.push({ id: pointId, paths: [pointPosition] });

				if (colorMode === "layer-type") {
					// Simple marker with colored dot, no sample metadata
					allPoints.push({
						id: pointId,
						position: pointPosition,
						iconColor: isSelected ? "error" : undefined,
						sampleId: "",
						barcode: "",
						hasChange: false,
						changeType: "",
						changeReason: "",
						simpleColor: LAYER_COLORS.points,
					});
				} else {
					// Per-job mode: extract sample metadata
					const sampleId = getShapeFeatureProperty(feature, "spl id");
					const barcode = getShapeFeatureProperty(feature, "spl brcd");
					const change = getShapeFeatureProperty(feature, "change");
					const { hasChange, changeType, changeReason } = parseChangeInfo(change);

					allPoints.push({
						id: pointId,
						position: pointPosition,
						iconColor: isSelected ? "error" : hasChange ? "warning" : undefined,
						sampleId,
						barcode,
						hasChange,
						changeType,
						changeReason,
					});
				}
			});
		}

		// Process each job's geodata
		effectiveJobs.forEach((job) => {
			const jobId = job.id || "unknown";

			// Process boundary
			const boundary = job.files?.boundaryGeoJSON as
				| FeatureCollection<Polygon>
				| FeatureCollection<MultiPolygon>
				| undefined;
			if (boundary) {
				processPolygonFeatures(boundary, job, jobId, "boundary");
			}

			// Process zones
			const zones = job.files?.zonesGeoJSON as FeatureCollection<Polygon> | FeatureCollection<MultiPolygon> | undefined;
			if (zones) {
				processPolygonFeatures(zones, job, jobId, "zones");
			}

			// Process points
			const points = job.files?.pointsGeoJSON as FeatureCollection<Point> | undefined;
			if (points) {
				processPointFeatures(points, jobId);
			}
		});

		setMapPoints(allPoints);
		setMapPolygons(allPolygons);
		setMapPolylines([]);
		setPolygonMetadata(allMetadata);

		if (map && effectiveJobs.length > 0 && (allPolygons.length > 0 || allPoints.length > 0)) {
			// Wait for the map to be fully rendered before fitting bounds
			const idleListener = google.maps.event.addListenerOnce(map, "idle", () => {
				map.fitBounds(mapBounds, boundsPadding);
			});

			const timeoutId = setTimeout(() => {
				map.fitBounds(mapBounds, boundsPadding);
			}, 200);

			return () => {
				google.maps.event.removeListener(idleListener);
				clearTimeout(timeoutId);
			};
		}
	}, [
		effectiveJobs,
		selectedFeatureIdsKey,
		props.lassoMode,
		map,
		mapsLibrary,
		colorMode,
		boundsPadding,
		isBoundaryMode,
	]);

	// Effect: Update visible markers based on bounds and zoom
	useEffect(() => {
		if (!map || !mapsLibrary) return;

		const currentBounds = map.getBounds();
		if (!currentBounds) return;

		const currentZoom = map.getZoom();

		// Filter mapPoints to only those in bounds and above zoom threshold
		const filteredMarkers = mapPoints.filter((marker) => {
			return isMarkerVisible(marker.position, currentBounds, currentZoom, props.zoomLevelToDisplayPoints);
		});

		setVisibleMapMarkers(filteredMarkers);
		setVisibleMapPolygons(mapPolygons);
	}, [zoom, bounds, map, mapsLibrary, mapPoints, mapPolygons, props.zoomLevelToDisplayPoints]);

	// Handlers
	const onBoundsChanged = (newBounds: google.maps.LatLngBoundsLiteral) => {
		setBounds(newBounds);
	};

	const onZoomChanged = (newZoom: number) => {
		setZoom(newZoom);
	};

	// Complete the lasso selection - check which features are inside the drawn polygon
	const completeLasso = useCallback(() => {
		const currentPath = lassoPathRef.current;

		if (!geometryLibrary || currentPath.length < 3) {
			lassoPathRef.current = [];
			return;
		}

		// Check which features are entirely contained in the lasso (all vertices inside)
		const lassoPolygon = new google.maps.Polygon({ paths: currentPath });
		const selectedIds = new Set<string>();

		polygonMetadata.forEach((meta) => {
			const isFullyInside = meta.paths.every((point) => {
				return geometryLibrary.poly.containsLocation(new google.maps.LatLng(point.lat, point.lng), lassoPolygon);
			});
			if (isFullyInside) {
				selectedIds.add(meta.id);
			}
		});

		// When toolbar is active, update internal state + notify parent
		if (showToolbar) {
			setEffectiveSelectedFeatureIds(selectedIds);
			setInternalLassoMode(false);
		}
		props.onLassoComplete?.(selectedIds);
		lassoPathRef.current = [];
	}, [polygonMetadata, geometryLibrary, props, showToolbar, setEffectiveSelectedFeatureIds]);

	// Keep completeLassoRef always pointing to latest completeLasso
	completeLassoRef.current = completeLasso;

	const onMapClick = useCallback(() => {
		// Lasso mode clicks are handled by native event listeners
		if (effectiveLassoMode) {
			return;
		}

		// Normal click behavior
		setActivePopup(null);
		window.postMessage({ type: "closeInfoWindow", source: "map-click" }, "*");
	}, [effectiveLassoMode]);

	const onInfoWindowClose = () => {
		setActivePopup(null);
	};

	const onPolygonClick = useCallback((job: Job, position: google.maps.LatLngLiteral) => {
		setActivePopup({ job, position });
	}, []);

	// Toolbar handler: toggle lasso or clear selection
	const handleToggleLasso = useCallback(() => {
		if (effectiveSelectedFeatureIds.size > 0) {
			setEffectiveSelectedFeatureIds(new Set());
		} else {
			setInternalLassoMode((prev) => !prev);
		}
	}, [effectiveSelectedFeatureIds.size, setEffectiveSelectedFeatureIds]);

	// Point-and-click lasso: click to add vertices, click near first point to close
	useEffect(() => {
		if (!map || !effectiveLassoMode) return;

		map.setOptions({ draggable: false });

		// Create native polyline for lasso visualization
		const lassoPolyline = new google.maps.Polyline({
			map,
			strokeColor: "#2196f3",
			strokeWeight: 2,
			strokeOpacity: 0.8,
		});

		// Create a marker for the first point (click target to close polygon)
		let firstPointMarker: google.maps.Marker | null = null;

		const handleClick = (e: google.maps.MapMouseEvent) => {
			if (!e.latLng) return;

			const clickedPoint = e.latLng.toJSON();

			// If we have 3+ points and click near the first point, close the polygon
			if (lassoPathRef.current.length >= 3) {
				const firstPoint = lassoPathRef.current[0];
				const distance = Math.sqrt(
					Math.pow(clickedPoint.lat - firstPoint.lat, 2) + Math.pow(clickedPoint.lng - firstPoint.lng, 2),
				);
				// Close if within ~0.0005 degrees (~50m at equator, scales with zoom)
				const zoom = map.getZoom() || 10;
				const threshold = 0.01 / Math.pow(2, zoom - 10); // Smaller threshold at higher zoom
				if (distance < threshold) {
					completeLassoRef.current();
					return;
				}
			}

			// Add the point
			lassoPathRef.current.push(clickedPoint);
			lassoPolyline.setPath(lassoPathRef.current);

			// Create/update first point marker
			if (lassoPathRef.current.length === 1) {
				firstPointMarker = new google.maps.Marker({
					position: clickedPoint,
					map,
					icon: {
						path: google.maps.SymbolPath.CIRCLE,
						scale: 8,
						fillColor: "#2196f3",
						fillOpacity: 1,
						strokeColor: "#ffffff",
						strokeWeight: 2,
					},
					title: "Click to close polygon",
				});
				// Clicking the marker closes the polygon
				firstPointMarker.addListener("click", () => {
					if (lassoPathRef.current.length >= 3) {
						completeLassoRef.current();
					}
				});
			}
		};

		const clickListener = map.addListener("click", handleClick);

		return () => {
			google.maps.event.removeListener(clickListener);
			lassoPolyline.setMap(null);
			if (firstPointMarker) firstPointMarker.setMap(null);
			map.setOptions({ draggable: true });
			lassoPathRef.current = [];
		};
	}, [map, effectiveLassoMode]);

	return {
		// State
		mapPoints,
		mapPolygons,
		visibleMapMarkers,
		visibleMapPolygons,
		visiblePolylines: mapPolylines,
		center,
		zoom,
		bounds,
		activePopup,
		mapsLibraryLoaded: !!mapsLibrary,
		polygonMetadata,

		// Computed from props
		hasGeodata,
		effectiveJobs,
		colorMode,
		disableDefaultUI,
		showEmptyState,
		zoomControl,
		resolvedHeight,
		boundsPadding,
		gestureHandling,

		// Toolbar state
		showToolbar,
		effectiveLassoMode,
		effectiveSelectedFeatureIds,
		onToggleLasso: handleToggleLasso,

		// Handlers
		onBoundsChanged,
		onZoomChanged,
		onMapClick,
		onInfoWindowClose,
		onPolygonClick,
	};
}
